import React from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import { Box, Paper, Typography, Card, CardContent } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomAppBar from "../../components/AppBar";
import api from "../../globals/axios";
import { useAlert } from "../../components/Alert";
import FarmerFarmAccordion from "../../components/Admin/FarmerFarmAccordion";
import { isNotNullOrEmptyArray } from "../../globals/utils";

const useStyles = {
  root: {
    width: "100%",
    minHeight: "100vh",
    marginBottom: "-4rem",
  },
  container: {
    padding: "20px",
    maxWidth: "1200px",
    margin: "0 auto",
  },
  title: {
    textAlign: "center",
    padding: "20px 0",
    borderRadius: "4px",
    marginBottom: "20px",
    border: "1px solid rgba(0, 0, 0, 0.12)",
  },
  detailsCard: {
    marginBottom: "20px",
    "& .MuiTypography-root": {
      marginBottom: "8px",
    },
    border: "1px solid rgba(0, 0, 0, 0.12)",
    boxShadow: "none",
  },
};

const DetailedFarmerView = () => {
  const { farmerId } = useParams();
  const { t } = useTranslation();
  const { showAlert } = useAlert();
  const [farmerDetails, setFarmerDetails] = useState([]);
  const [farmDetails, setFarmDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch Farmer Details
  const fetchFarmerDetails = async (id) => {
    try {
      setIsLoading(true);
      const response = await api.get(
        `/app-admin/get-farmer-details-by-id/${id}`
      );
      if (response?.data?.data) {
        let { data } = response.data;
        let { farms, ...farmer } = data;
        let farmerDetails = [
          { label: t("Name"), value: farmer.farmer_name || t("Not Available") },
          { label: t("Mobile"), value: farmer.mobile || t("Not Available") },
          { label: t("Email"), value: farmer.email || t("Not Available") },
          { label: t("Village"), value: farmer.village || t("Not Available") },
          {
            label: t("Area Mapped"),
            value: `${farmer.area || "N/A"} acres`,
          },
          {
            label: t("Total Farms"),
            value: farmer.farms_count || t("Not Available"),
          },
          { label: t("Crops"), value: farmer.crops || t("Not Available") },
          {
            label: t("POP Manager"),
            value: farmer.pop_manager || t("Not Available"),
          },
          {
            label: t("Area Manager"),
            value: farmer.area_manager || t("Not Available"),
          },
          { label: t("VLE"), value: farmer.vle || t("Not Available") },
        ];
        let farmDetails = farms.map((farm) => {
          return {
            id: farm.farm_id,
            [t("Farm Id")]: farm.farm_id,
            [t("Farm Name")]: farm.farm_name,
            [t("Area")]: farm.area,
            [t("Is Approved")]: farm.is_approved ? t("Yes") : t("No"),
          };
        });
        setFarmerDetails(farmerDetails);
        console.log(farmDetails);
        setFarmDetails(farmDetails);
      } else {
        showAlert("error", t("Error fetching farmer details. Data not found!"));
      }
    } catch (error) {
      console.error("Error fetching farmer details:", error);
      showAlert(
        "error",
        t("Couldn't fetch the farmer details. Please try again later!")
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (farmerId) {
      fetchFarmerDetails(farmerId);
    }
  }, [farmerId]);

  return (
    <Box sx={useStyles.root} component={Paper}>
      <Helmet>
        <title>Farmer Details - Farmapp</title>
      </Helmet>
      <CustomAppBar />
      <Box sx={useStyles.container}>
        {isLoading ? (
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h6">{t("Loading...")}</Typography>
          </Box>
        ) : (
          <>
            {/* Title Section */}
            <Paper sx={useStyles.title} elevation={0}>
              <Typography variant="h5">{t("Farmer Details")}</Typography>
            </Paper>

            {/* Farmer Details Section */}
            <Card sx={useStyles.detailsCard}>
              <CardContent>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: 1,
                  }}
                >
                  {farmerDetails.map((detail, index) => (
                    <Box
                      key={index}
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <Typography variant="body1">
                        <strong>{detail.label}:</strong> {detail.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>

            {/* Farm Details Section */}
            {isNotNullOrEmptyArray(farmDetails) ? (
              <FarmerFarmAccordion farms={farmDetails} />
            ) : (
              <Box sx={{ textAlign: "center" }}>
                {t("No farms found for this farmer")}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default DetailedFarmerView;
